/**
 * Seeded deterministic PRNG for the demo world.
 *
 * Everything the demo engine synthesises — addresses, trade timings, candle
 * noise, holder distributions — is drawn from one of these generators. That is a
 * hard requirement, not a convenience: the world must be byte-identical on every
 * reload so screenshots, visual diffs and unit tests are stable, and so a token's
 * chart does not change shape when the user navigates away and back.
 *
 * mulberry32 is used because it is 32-bit-integer only (no BigInt, no float
 * accumulation), which means identical output across every JS engine.
 */

export interface Rng {
  /** Raw uniform in [0, 1). */
  rand(): number;
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** Uniform float in [min, max). */
  float(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  /** Picks by relative weight. Weights need not be normalised. */
  pickWeighted<T>(items: readonly T[], weights: readonly number[]): T;
  /** Normal deviate via Box–Muller. */
  gauss(mean?: number, sd?: number): number;
  /** True with probability `p`. */
  bool(p?: number): boolean;
  /** Fisher–Yates on a copy — never mutates the input. */
  shuffle<T>(items: readonly T[]): T[];
  /** A syntactically valid 20-byte address. */
  hexAddress(): `0x${string}`;
  /** A syntactically valid 32-byte tx hash. */
  txHash(): `0x${string}`;
  /** Derives an independent child generator, so one sub-system's draw count
   *  cannot shift another's stream (the usual cause of "everything moved"). */
  fork(label: string): Rng;
}

/** FNV-1a-ish string hash, used to turn labels/addresses into 32-bit seeds. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Accepts a string so callers can seed from an address without pre-hashing. */
export function makeRng(seed: number | string): Rng {
  let state = (typeof seed === 'string' ? hashString(seed) : Math.floor(seed) >>> 0) || 0x9e3779b9;

  const rand = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const HEX = '0123456789abcdef';
  const hex = (chars: number): string => {
    let out = '';
    for (let i = 0; i < chars; i++) out += HEX[Math.floor(rand() * 16)];
    return out;
  };

  const self: Rng = {
    rand,
    int: (min, max) => (max <= min ? min : min + Math.floor(rand() * (max - min + 1))),
    float: (min, max) => min + rand() * (max - min),
    pick: <T,>(items: readonly T[]): T => items[Math.floor(rand() * items.length)] as T,
    pickWeighted: <T,>(items: readonly T[], weights: readonly number[]): T => {
      let total = 0;
      for (let i = 0; i < items.length; i++) total += Math.max(0, weights[i] ?? 0);
      if (total <= 0) return items[0] as T;
      let roll = rand() * total;
      for (let i = 0; i < items.length; i++) {
        roll -= Math.max(0, weights[i] ?? 0);
        if (roll <= 0) return items[i] as T;
      }
      return items[items.length - 1] as T;
    },
    gauss: (mean = 0, sd = 1) => {
      // u is nudged off zero so log() never returns -Infinity.
      const u = 1 - rand();
      const v = rand();
      return mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    },
    bool: (p = 0.5) => rand() < p,
    shuffle: <T,>(items: readonly T[]): T[] => {
      const out = items.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = out[i] as T;
        out[i] = out[j] as T;
        out[j] = tmp;
      }
      return out;
    },
    hexAddress: () => `0x${hex(40)}` as `0x${string}`,
    txHash: () => `0x${hex(64)}` as `0x${string}`,
    fork: (label: string) => makeRng((hashString(label) ^ Math.floor(rand() * 0xffffffff)) >>> 0),
  };

  return self;
}

/** Convenience for the many "seed from this token's address" call sites. */
export function rngForAddress(address: string, salt = ''): Rng {
  return makeRng(`${address.toLowerCase()}:${salt}`);
}
